import cls from 'classnames';
import React, {
  forwardRef,
  InputHTMLAttributes,
  useEffect,
  useState,
} from 'react';

import { EyeIcon, EyeOffIcon } from '@/components/icons/eye-icon';

import FormElement, { InputVariant } from '..';
import styles from '../styles';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  showLabel?: boolean;
  variant?: InputVariant;
  disabled?: boolean;
  containerClassName?: string;
  className?: string;
  helperText?: string;
  error?: string;
  submitFailed?: boolean;
  showPasswordToggle?: boolean;
}

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      id,
      label,
      showLabel,
      variant = 'newsletter',
      disabled,
      containerClassName,
      className,
      helperText,
      error,
      submitFailed,
      showPasswordToggle = false,
      type,
      onBlur,
      ...restProps
    },
    ref,
  ) => {
    const [blur, setBlur] = useState(false);
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const hasError = !!error;
    const isPasswordField = type === 'password';

    let inputType = type;

    if (showPasswordToggle && isPasswordField) {
      inputType = isPasswordVisible ? 'text' : 'password';
    }

    const handleBlur: React.FocusEventHandler<HTMLInputElement> = (event) => {
      setBlur(true);
      onBlur?.(event);
    };

    useEffect(() => {
      if (submitFailed) setBlur(true);
    }, [submitFailed]);

    const input = (
      <input
        {...restProps}
        ref={ref}
        id={id}
        type={inputType}
        disabled={disabled}
        className={cls(
          styles.input(variant, hasError, blur),
          showPasswordToggle && isPasswordField && 'pr-12',
          className,
          'placeholder-gray-3',
        )}
        onBlur={handleBlur}
      />
    );

    return (
      <FormElement
        id={id}
        label={label}
        showLabel={showLabel}
        className={containerClassName}
        helperText={helperText}
        error={error}
        blur={blur}
      >
        {showPasswordToggle && isPasswordField ? (
          <div className="relative">
            {input}
            <button
              type="button"
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-2 hover:text-white"
              onClick={() => setIsPasswordVisible((visible) => !visible)}
              aria-label={
                isPasswordVisible ? 'Passwort verbergen' : 'Passwort anzeigen'
              }
            >
              {isPasswordVisible ? (
                <EyeOffIcon className="w-6 h-6" />
              ) : (
                <EyeIcon className="w-6 h-6" />
              )}
            </button>
          </div>
        ) : (
          input
        )}
      </FormElement>
    );
  },
);

export default Input;
